/**
 * Slide-in detail drawer for the /admin/stage-jobs list page.
 *
 * Lives as a right-side panel within StageJobsPage's DOM rather than
 * a modal — matches the in-page expand-on-click pattern DiagnosticPage
 * uses, and keeps focus management simple (no portal, no a11y modal
 * traps to maintain).
 *
 * Live-progress integration: when the job's terminal status is
 * 'running' (or 'queued'), the drawer consumes useJobStatus to refresh
 * the live step + provider + fallback_count every 5s. Once the job
 * crosses to a terminal status the hook stops polling and the
 * step_history snapshot from stage_jobs becomes the canonical surface.
 *
 * Re-run action: confirmation modal in-place (a one-shot dialog
 * within the drawer body rather than a separate component). Only
 * surfaces when status ∈ {failed, refused_all_providers, cancelled}
 * — matches the backend's rerun guard at server.py:4636.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  RotateCcw,
  X,
  XCircle,
} from 'lucide-react';
import {
  adminGetStageJob,
  adminRerunStageJob,
  type StageJobRow,
  type StageJobDetailResponse,
} from '@/api/client';
import useJobStatus from '@/hooks/useJobStatus';


interface Props {
  /** rq_job_id of the selected row; null hides the drawer. */
  jobId: string | null;
  onClose: () => void;
  /** Notify the list page to refresh after a successful rerun. */
  onRerunQueued?: () => void;
}


const STATUS_CLASS: Record<string, string> = {
  succeeded:             'bg-emerald-100 text-emerald-800 border-emerald-300',
  running:               'bg-sky-100 text-sky-800 border-sky-300',
  failed:                'bg-rose-100 text-rose-800 border-rose-300',
  refused_all_providers: 'bg-amber-100 text-amber-800 border-amber-300',
  queued:                'bg-zinc-100 text-zinc-700 border-zinc-300',
  cancelled:             'bg-zinc-100 text-zinc-600 border-zinc-300',
};

const RERUNNABLE_STATES = new Set([
  'failed', 'refused_all_providers', 'cancelled',
]);


function _statusIcon(status: string): React.ReactNode {
  switch (status) {
    case 'succeeded':
      return <CheckCircle2 size={12} className="text-emerald-600" />;
    case 'failed':
    case 'refused_all_providers':
    case 'cancelled':
      return <XCircle size={12} className="text-rose-600" />;
    case 'running':
      return <Loader2 size={12} className="text-sky-600 animate-spin" />;
    default:
      return null;
  }
}


function _ts(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}


function _stepTs(step: { ts: number }): string {
  try {
    return new Date(step.ts * 1000).toLocaleTimeString();
  } catch {
    return String(step.ts);
  }
}


export default function StageJobDetailDrawer({
  jobId, onClose, onRerunQueued,
}: Props) {
  const [detail, setDetail] = useState<StageJobDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRerun, setConfirmRerun] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [rerunToast, setRerunToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await adminGetStageJob(jobId);
      setDetail(r);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load stage job';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    setDetail(null);
    setConfirmRerun(false);
    setRerunToast(null);
    if (jobId) void load();
  }, [jobId, load]);

  // Live-status hook — keeps current step / provider / fallback_count
  // fresh while the job is in flight. Hook itself stops polling when
  // the job reaches a terminal status; we surface its payload directly.
  const liveStatus = useJobStatus(
    jobId && detail?.stage_job?.status === 'running' ? jobId : null,
    { pollInterval: 5000 },
  );

  async function handleRerun() {
    if (!jobId) return;
    setRerunning(true);
    setError(null);
    try {
      const r = await adminRerunStageJob(jobId);
      setRerunToast(`Re-enqueued ${r.rq_job_id}`);
      setConfirmRerun(false);
      // Reload detail so the new lifecycle observations land here.
      await load();
      onRerunQueued?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Re-run failed';
      setError(msg);
    } finally {
      setRerunning(false);
    }
  }

  if (!jobId) return null;

  const job: StageJobRow | undefined = detail?.stage_job;
  const stepHistory = job?.step_history || [];
  const isRunning = job?.status === 'running';
  const canRerun = job && RERUNNABLE_STATES.has(job.status);

  return (
    <aside
      data-testid="admin-stage-job-detail-drawer"
      data-job-id={jobId}
      className="fixed inset-y-0 right-0 w-[480px] bg-white border-l border-zinc-200 shadow-xl z-30 overflow-auto flex flex-col"
    >
      {/* Header */}
      <div className="border-b border-zinc-200 px-4 py-2 flex items-center gap-2 sticky top-0 bg-white">
        <h2 className="text-sm font-semibold text-zinc-900 flex-1 truncate" title={jobId}>
          {job ? `${job.module} · ${job.stage}` : 'Loading…'}
        </h2>
        {job && (
          <span
            data-testid="admin-stage-job-detail-status"
            className={
              'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] ' +
              'font-medium border rounded ' +
              (STATUS_CLASS[job.status] || 'bg-zinc-100')
            }
          >
            {_statusIcon(job.status)}
            {job.status}
          </span>
        )}
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          title="Reload"
          className="text-xs text-zinc-600 hover:text-zinc-900 disabled:opacity-50"
          data-testid="admin-stage-job-detail-reload"
        >
          {loading
            ? <Loader2 size={12} className="animate-spin" />
            : <RefreshCw size={12} />}
        </button>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          data-testid="admin-stage-job-detail-close"
          className="text-zinc-500 hover:text-zinc-900"
        >
          <X size={16} />
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          data-testid="admin-stage-job-detail-error"
          className="bg-rose-50 border-b border-rose-200 text-rose-800 text-xs px-4 py-2"
        >
          {error}
        </div>
      )}

      {/* Rerun toast */}
      {rerunToast && (
        <div
          data-testid="admin-stage-job-detail-rerun-toast"
          className="bg-emerald-50 border-b border-emerald-200 text-emerald-800 text-xs px-4 py-2"
        >
          {rerunToast}
        </div>
      )}

      {/* Body */}
      <div className="p-4 flex-1 space-y-4 text-xs">
        {!job && loading && (
          <div className="text-zinc-500 inline-flex items-center gap-1">
            <Loader2 size={12} className="animate-spin" />
            Loading job detail…
          </div>
        )}

        {job && (
          <>
            {/* Metadata */}
            <section data-testid="admin-stage-job-detail-meta">
              <h3 className="text-[10px] font-semibold uppercase text-zinc-500 mb-1.5">
                Metadata
              </h3>
              <dl className="grid grid-cols-[120px_1fr] gap-y-1 gap-x-2 text-zinc-700">
                <dt className="text-zinc-500">rq_job_id</dt>
                <dd className="font-mono break-all">{job.rq_job_id}</dd>
                <dt className="text-zinc-500">matter_id</dt>
                <dd className="font-mono">{job.matter_id || '—'}</dd>
                <dt className="text-zinc-500">action</dt>
                <dd className="font-mono">{job.action}</dd>
                <dt className="text-zinc-500">created</dt>
                <dd>{_ts(job.created_at)}</dd>
                <dt className="text-zinc-500">started</dt>
                <dd>{_ts(job.started_at)}</dd>
                <dt className="text-zinc-500">finished</dt>
                <dd>{_ts(job.finished_at)}</dd>
                <dt className="text-zinc-500">provider</dt>
                <dd>{job.final_provider || '—'}</dd>
                <dt className="text-zinc-500">fallback</dt>
                <dd>
                  {job.fallback_count > 0 ? (
                    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded text-[10px]">
                      <AlertTriangle size={9} />×{job.fallback_count}
                    </span>
                  ) : '0'}
                </dd>
                {job.error_class && (
                  <>
                    <dt className="text-zinc-500">error_class</dt>
                    <dd className="font-mono text-rose-700">{job.error_class}</dd>
                  </>
                )}
              </dl>
            </section>

            {/* Live progress (only while running) */}
            {isRunning && liveStatus.status?.progress && (
              <section data-testid="admin-stage-job-detail-live-progress">
                <h3 className="text-[10px] font-semibold uppercase text-zinc-500 mb-1.5">
                  Live progress
                </h3>
                <div className="border border-sky-200 bg-sky-50 rounded p-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <Loader2 size={12} className="text-sky-600 animate-spin" />
                    <span className="font-mono text-[10px] text-sky-800">
                      {liveStatus.status.progress.step}
                    </span>
                    {liveStatus.status.progress.provider && (
                      <span className="ml-auto px-1.5 py-0.5 text-[10px] font-mono bg-white text-zinc-700 rounded">
                        {liveStatus.status.progress.provider}
                      </span>
                    )}
                  </div>
                  {liveStatus.status.progress.message && (
                    <p className="text-zinc-700">{liveStatus.status.progress.message}</p>
                  )}
                </div>
              </section>
            )}

            {/* Step history (terminal snapshot) */}
            {stepHistory.length > 0 && (
              <section data-testid="admin-stage-job-detail-step-history">
                <h3 className="text-[10px] font-semibold uppercase text-zinc-500 mb-1.5">
                  Step history
                </h3>
                <ol className="space-y-1 border-l-2 border-zinc-200 pl-3">
                  {stepHistory.map((step, idx) => (
                    <li
                      key={idx}
                      data-testid={`admin-stage-job-detail-step-${idx}`}
                      className="relative -ml-[7px] pl-3"
                    >
                      <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-zinc-400" />
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[10px] text-zinc-500">
                          {_stepTs(step)}
                        </span>
                        <span className="font-mono font-medium text-zinc-700">
                          {step.step}
                        </span>
                      </div>
                      {step.message && (
                        <p className="text-zinc-600 mt-0.5">{step.message}</p>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* Error block (failed / refused) */}
            {job.error_message && (
              <section data-testid="admin-stage-job-detail-error-message">
                <h3 className="text-[10px] font-semibold uppercase text-zinc-500 mb-1.5">
                  Error details
                </h3>
                <pre className="text-[10px] bg-rose-50 border border-rose-200 text-rose-900 rounded p-2 whitespace-pre-wrap break-all">
                  {job.error_message}
                </pre>
              </section>
            )}

            {/* Result summary (succeeded) */}
            {job.result_summary && Object.keys(job.result_summary).length > 0 && (
              <section data-testid="admin-stage-job-detail-result">
                <h3 className="text-[10px] font-semibold uppercase text-zinc-500 mb-1.5">
                  Result summary
                </h3>
                <pre className="text-[10px] bg-zinc-50 border border-zinc-200 text-zinc-800 rounded p-2 whitespace-pre-wrap break-all">
                  {JSON.stringify(job.result_summary, null, 2)}
                </pre>
              </section>
            )}

            {/* Params summary */}
            {job.params_summary && Object.keys(job.params_summary).length > 0 && (
              <section data-testid="admin-stage-job-detail-params">
                <h3 className="text-[10px] font-semibold uppercase text-zinc-500 mb-1.5">
                  Params
                </h3>
                <pre className="text-[10px] bg-zinc-50 border border-zinc-200 text-zinc-700 rounded p-2 whitespace-pre-wrap break-all">
                  {JSON.stringify(job.params_summary, null, 2)}
                </pre>
              </section>
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      {job && (
        <div className="border-t border-zinc-200 px-4 py-2 bg-white sticky bottom-0 flex items-center gap-2">
          {canRerun && !confirmRerun && (
            <button
              type="button"
              data-testid="admin-stage-job-detail-rerun"
              onClick={() => setConfirmRerun(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-zinc-300 rounded hover:bg-zinc-50"
            >
              <RotateCcw size={12} />
              Re-run
            </button>
          )}
          {confirmRerun && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-rose-700">
                Re-run? Generates new LLM costs.
              </span>
              <button
                type="button"
                data-testid="admin-stage-job-detail-rerun-cancel"
                onClick={() => setConfirmRerun(false)}
                disabled={rerunning}
                className="px-2 py-0.5 border border-zinc-300 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="admin-stage-job-detail-rerun-confirm"
                onClick={() => void handleRerun()}
                disabled={rerunning}
                className="px-2 py-0.5 border border-rose-300 bg-rose-50 text-rose-800 rounded inline-flex items-center gap-1 disabled:opacity-50"
              >
                {rerunning && <Loader2 size={10} className="animate-spin" />}
                Confirm re-run
              </button>
            </div>
          )}
          {!canRerun && (
            <span className="text-[10px] text-zinc-500 italic">
              Re-run available only for failed / refused / cancelled jobs.
            </span>
          )}
        </div>
      )}
    </aside>
  );
}
